import { Briefcase, ChevronRight } from 'lucide-react'
import { GESTOR_SETOR_LABELS } from '@/config/gestorAccess'
import {
  GESTOR_TABLE_TH_CLASS,
  GESTOR_TABLE_TR_CLASS,
  GestorListTableShell,
  GestorTableAvatar,
} from '@/components/Gestores/GestorListTableShell'
import type { GestorEquipeRow } from '@/services/gestoresService'
import { cn } from '@/utils/cn'

const STATUS_BADGE_CLASS: Record<string, string> = {
  ATIVO: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  INATIVO: 'bg-[var(--glass-bg)] text-[var(--text-muted)] border-[var(--glass-border)]',
  NAO_PREENCHIDO: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
}

const STATUS_LABELS: Record<string, string> = {
  ATIVO: 'Ativo',
  INATIVO: 'Inativo',
  NAO_PREENCHIDO: 'Não preenchido',
}

type Props = {
  equipe: GestorEquipeRow[]
  loading?: boolean
  onSelect: (id: string) => void
}

export function GestorEquipeTable({ equipe, loading, onSelect }: Props) {
  return (
    <GestorListTableShell
      loading={loading}
      loadingMessage='Carregando equipe…'
      empty={!loading && equipe.length === 0}
      emptyIcon={
        <Briefcase size={48} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
      }
      emptyTitle='Nenhum profissional no seu setor'
      emptyHint='Profissionais PJ vinculados ao seu time aparecem aqui'
    >
      <table className='w-full'>
        <thead>
          <tr className='border-b border-[var(--glass-border)]'>
            {['Profissional', 'Setor', 'Remuneração', 'Status', ''].map((h) => (
              <th
                key={h || 'actions'}
                className={GESTOR_TABLE_TH_CLASS}
                style={{ fontSize: 'var(--font-size-label)' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {equipe.map((member) => (
            <tr
              key={member.id}
              className={GESTOR_TABLE_TR_CLASS}
              onClick={() => onSelect(member.id)}
            >
              <td className='px-4 py-3'>
                <div className='flex items-center gap-3'>
                  <GestorTableAvatar name={member.nomeCompleto} />
                  <span
                    className='font-medium text-[var(--font-color-body)]'
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    {member.nomeCompleto}
                  </span>
                </div>
              </td>
              <td className='px-4 py-3'>
                <span
                  className='text-[var(--font-color-muted)]'
                  style={{ fontSize: 'var(--font-size-sm)' }}
                >
                  {member.setor
                    ? GESTOR_SETOR_LABELS[member.setor] ?? member.setor
                    : member.areaAtuacao ?? '—'}
                </span>
              </td>
              <td className='px-4 py-3'>
                <span
                  className='text-[var(--font-color-body)]'
                  style={{ fontSize: 'var(--font-size-sm)' }}
                >
                  {member.remuneracaoMensal != null
                    ? member.remuneracaoMensal.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })
                    : '—'}
                </span>
              </td>
              <td className='px-4 py-3'>
                <span
                  className={cn(
                    'rounded-md border px-2 py-0.5 text-[9px] font-black uppercase',
                    STATUS_BADGE_CLASS[member.status] ?? STATUS_BADGE_CLASS.INATIVO,
                  )}
                >
                  {STATUS_LABELS[member.status] ?? member.status}
                </span>
              </td>
              <td className='px-4 py-3'>
                <ChevronRight size={14} className='text-[var(--text-muted)]' aria-hidden />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </GestorListTableShell>
  )
}
